import { createContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Theme, config } from "../constants";

const METRIC = "metric";
const IMPERIAL = "imperial";

const initialSettings = {
  compact: true,
  compactView: false,
  direction: "ltr",
  responsiveFontSizes: true,
  roundedCorners: true,
  theme: Theme.LIGHT,
  iconColor: "Blue",
  accurateAddress: true,
  groupView: false,
  // Keep an unset choice distinct from an explicitly saved cluster setting.
  cluster: null,
  colouredCluster: true,
  navSidebarExpanded: true,
  fleetDeviceListWidth: 360,
  fleetAssetSortOrder: "current",
  plantOperatingViews: {},
  measurement: METRIC,
  ...config,
};

export const restoreSettings = () => {
  let settings = null;

  try {
    const storedData = window.localStorage.getItem("settings");

    if (storedData) {
      const parsedSettings = JSON.parse(storedData);

      settings = {
        ...parsedSettings,
        cluster:
          typeof parsedSettings.cluster === "boolean"
            ? parsedSettings.cluster
            : null,
        plantOperatingViews: parsedSettings.plantOperatingViews && typeof parsedSettings.plantOperatingViews === "object" && !Array.isArray(parsedSettings.plantOperatingViews) ? parsedSettings.plantOperatingViews : {},
        roundedCorners:
          typeof parsedSettings.roundedCorners === "boolean"
            ? parsedSettings.roundedCorners
            : true,
        fleetDeviceListWidth:
          typeof parsedSettings.fleetDeviceListWidth === "number"
            ? parsedSettings.fleetDeviceListWidth
            : 360,
        fleetAssetSortOrder:
          parsedSettings.fleetAssetSortOrder === "alphabetical"
            ? "alphabetical"
            : "current",
        // The legacy browser-wide selector has been replaced by per-vehicle
        // web colours. Keep non-vehicle assets on the existing blue artwork.
        iconColor: "Blue",
        ...config,
      };
    } else {
      settings = {
        ...config,
        compact: true,
        compactView: false,
        direction: "ltr",
        responsiveFontSizes: true,
        roundedCorners: true,
        theme: Theme.LIGHT,
        iconColor: "Blue",
        accurateAddress: true,
        groupView: false,
        cluster: null,
        colouredCluster: true,
        navSidebarExpanded: true,
        fleetDeviceListWidth: 360,
        fleetAssetSortOrder: "current",
        measurement: METRIC,

        //window.matchMedia("(prefers-color-scheme: dark)").matches
        // ? THEMES.DARK
        // : THEMES.LIGHT,
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings) => {
  window.localStorage.setItem("settings", JSON.stringify(settings));
};

const SettingsContext = createContext({
  settings: { ...initialSettings, ...config },
  saveSettings: () => {},
});

export const SettingsProvider = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState(
    () => restoreSettings() || initialSettings
  );

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings({ ...restoredSettings, ...config });
    }
  }, []);

  const saveSettings = (updatedSettings) => {
    const nextSettings = {
      ...settings,
      ...updatedSettings,
      iconColor: "Blue",
      ...config,
    };

    setSettings(nextSettings);
    storeSettings(nextSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
